import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { userState } from "../user/userSlice";

import { showNotification } from "../../components/Notification";
import { formatLabel } from "../../uitls/Utils";

import { sendOTP } from "../../api/userApi";
import useAuth from "../../hooks/useAuth";

const EmployeePassword = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { auth } = useAuth();
  const [formValues] = useState({
    corporateEmail: auth?.user?.corporateEmail || "",
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitReferenceState, setSubmitReferenceState] = useState(false);
  const stateOfUser = useSelector(userState);

  const requiredFields = {
    corporateEmail: true,
  };

  useEffect(() => {
    if (
      !stateOfUser.error &&
      submitReferenceState &&
      stateOfUser.status === "succeeded"
    ) {
      showNotification("success", "OTP Sent Successfully");
      navigate("/user-reset-password");
    }
    setSubmitReferenceState(false);
  }, [submitReferenceState]);

  const trimObjectValues = (obj) => {
    if (Array.isArray(obj)) {
      return obj.map(trimObjectValues);
    } else if (typeof obj === "object" && obj !== null) {
      return Object.keys(obj).reduce((acc, key) => {
        acc[key] = trimObjectValues(obj[key]);
        return acc;
      }, {});
    } else if (typeof obj === "string") {
      return obj.trim();
    }
    return obj;
  };

  const validateForm = () => {
    const newErrors = {};
    Object.keys(requiredFields).forEach((key) => {
      if (requiredFields[key]) {
        const value = formValues[key];
        if (typeof value === "string") {
          const trimmedValue = value.trim();
          if (!trimmedValue) {
            newErrors[key] = `${formatLabel(key)} is required`;
          }
          if (
            trimmedValue &&
            key === "newPassword" &&
            trimmedValue.length < 6
          ) {
            newErrors[key] = "Password must be at least 6 characters";
          }
          if (
            trimmedValue &&
            key === "confirmPassword" &&
            trimmedValue !== formValues?.newPassword
          ) {
            newErrors[key] = "Passwords do not match";
          }
        } else if (value === null || value === undefined || value === "") {
          newErrors[key] = `${formatLabel(key)} is required`;
        }
      }
    });

    setFormErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      dispatch(sendOTP(trimObjectValues({ email: formValues?.corporateEmail })))
        .unwrap()
        .then(() => {
          setSubmitReferenceState(true);
        })
        .catch((error) => {
          console.error("Error:", error);
          showNotification(
            "error",
            error?.message || "Something went wrong..."
          );
        });
    }
  };

  return (
    <div className="h-screen flex items-start justify-center p-4 md:p-8 overflow-hidden">
      {/* Main Container with Shadow */}
      <div className="w-full max-w-6xl bg-white rounded-2xl md:rounded-3xl shadow-2xl overflow-hidden relative">
        <div className="flex flex-col lg:flex-row h-full">
          {/* Form Section - Left */}
          <div className="w-full lg:w-1/2 flex items-start justify-center p-6 md:p-8 lg:p-16">
            <div className="w-full max-w-md">
              {/* Form Title */}
              <div className="text-center mb-4 md:mb-6 lg:mb-8">
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2 md:mb-3">
                  Change Your Password
                </h2>
                <p className="text-sm md:text-base text-gray-600">
                  Secure your account with a new password
                </p>
              </div>

              {/* Description */}
              <div className="mb-6 md:mb-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-gray-600 text-center">
                  Enter your new password and confirm it to proceed with the
                  password change. We'll send a verification code to your email.
                </p>
              </div>

              {/* Form Fields */}
              <div className="space-y-3 md:space-y-4 lg:space-y-6">
                {/* Email Field */}
                <div>
                  <label
                    htmlFor="corporate-email-input"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Organizational Email
                  </label>
                  <div className="relative">
                    <input
                      id="corporate-email-input"
                      type="email"
                      value={formValues?.corporateEmail || ""}
                      readOnly={true}
                      className="block w-full px-3 md:px-4 py-3 md:py-4 border border-gray-300 rounded-lg md:rounded-xl bg-gray-50 text-gray-600 cursor-not-allowed text-base md:text-lg"
                    />
                  </div>
                  {formErrors?.corporateEmail && (
                    <p className="mt-1 md:mt-2 text-sm text-red-600">
                      {formErrors.corporateEmail}
                    </p>
                  )}
                </div>
              </div>

              {/* Loading Indicator */}
              <div className="flex justify-center items-center my-3 md:my-4"></div>

              {/* Submit Button */}
              <button
                onClick={handleSubmit}
                disabled={stateOfUser?.loading}
                className="w-full mt-2 md:mt-3 py-3 md:py-4 px-4 brand-btn-primary text-white font-medium rounded-lg md:rounded-xl transition-all duration-200 transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed text-base md:text-lg ripple"
              >
                {stateOfUser?.loading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-4 w-4 md:h-5 md:w-5 border-2 border-white border-t-transparent mr-2"></div>
                    <span>Sending...</span>
                  </div>
                ) : (
                  "Send Verification Code"
                )}
              </button>
            </div>
          </div>

          {/* Blue Design Section - Right - Hidden on Mobile */}
          <div className="hidden lg:block w-1/2 relative overflow-hidden">
            {/* Large Blue Abstract Shape */}
            <div className="absolute top-1/2 right-0 transform translate-x-1/2 -translate-y-1/2 w-[180%] h-[122%] brand-sidebar rounded-full"></div>

            {/* White Text Overlay */}
            <div className="relative z-10 h-full flex flex-col justify-center items-center px-16">
              <div className="text-white text-center max-w-md">
                <h1 className="text-4xl font-bold mb-4 tracking-wide ml-7">
                  Password Security
                </h1>
                <p className="text-xl leading-relaxed text-left mb-8 ml-15">
                  Strengthening Your Account Protection
                </p>
                <div className="space-y-3 text-left ml-15">
                  <div className="flex items-center">
                    <div className="w-2 h-2 bg-blue-300 rounded-full mr-3"></div>
                    <span className="text-lg">Secure Password Reset</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-2 h-2 bg-blue-300 rounded-full mr-3"></div>
                    <span className="text-lg">Email Verification</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-2 h-2 bg-blue-300 rounded-full mr-3"></div>
                    <span className="text-lg">Account Security</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-2 h-2 bg-blue-300 rounded-full mr-3"></div>
                    <span className="text-lg">Data Protection</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Small Blue Spheres - Outside Abstract Shape - Hidden on Mobile */}
        <div className="hidden lg:block absolute bottom-8 left-1/2 transform -translate-x-1/2 flex space-x-12 z-20 ml-4">
          <div className="w-16 h-16 brand-sidebar rounded-full"></div>
          <div className="w-12 h-12 brand-sidebar rounded-full"></div>
        </div>
      </div>
    </div>
  );
};

export default EmployeePassword;
